import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from "react-router-dom";
import './styles/documentViewer.scss'
import { Document, Page, pdfjs } from 'react-pdf';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { readJson } from '../../apiHelper/readJson';

// A faint, tiled, diagonal watermark of the learner's email overlays every page, so any
// screenshot or photo of the material is traceable back to whoever leaked it. Built as an
// inline SVG tile that repeats — survives cropping and scales with the document height.
function watermarkStyle(email: string): React.CSSProperties {
    const label = email
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const tile =
        `<svg xmlns='http://www.w3.org/2000/svg' width='300' height='190'>` +
        `<text x='150' y='95' fill='rgba(20,20,20,0.10)' font-family='monospace' font-size='15'` +
        ` text-anchor='middle' transform='rotate(-30 150 95)'>${label}</text></svg>`;
    return {
        backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(tile)}")`,
        backgroundRepeat: 'repeat',
    };
}

// How far the user can zoom out/in from the auto-fitted "1×" baseline.
const ZOOM_MIN = 0.4;
const ZOOM_MAX = 4;

export default function DocumentViewer() {
    const [numPages, setNumPages] = useState<number>();
    const [file, setFile] = useState<Blob | string>();
    const [email, setEmail] = useState("");
    const [failed, setFailed] = useState(false);
    // zoom is a multiplier over "fit to box": 1 renders each page at the stage width, so
    // the document fits without horizontal scroll; the controls step it up/down from there.
    const [zoom, setZoom] = useState(1);
    const stageRef = useRef<HTMLDivElement>(null);
    const [fitWidth, setFitWidth] = useState(0);

    const api = new ApiHelper();
    const storedUrl = useMemo(() => localStorage.getItem("url"), []);

    pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

    // Fetch the PDF through the auth-gated endpoint (as an in-memory blob) instead of
    // pointing react-pdf at the public files URL. The endpoint serves both uploads and
    // external links (proxied server-side), so pdf.js is never blocked by a third party's
    // missing CORS headers. Also pull the email for the watermark.
    useEffect(() => {
        let revoked = false;
        (async () => {
            if (!storedUrl) { setFailed(true); return; }
            try {
                const blob = await api.fetchProtectedFile(storedUrl);
                if (!revoked) setFile(blob);
            } catch (x: any) {
                api.processErrors(x);
                setFailed(true);
            }
        })();

        (async () => {
            try {
                const res = await api.userApi.getDataList();
                const proc = await readJson(res);
                if (!revoked) setEmail(proc.email ?? "");
            } catch { /* watermark is best-effort; ignore */ }
        })();

        return () => { revoked = true; };
    }, []);

    // Discourage casual saving/printing of paid PDF materials (consistent with VideoPlayer).
    useEffect(() => {
        const blockContextmenu = (e: MouseEvent) => e.preventDefault();
        const blockShortcut = (e: KeyboardEvent) => {
            const k = e.key.toLowerCase();
            if ((e.ctrlKey || e.metaKey) && (k === 's' || k === 'p')) e.preventDefault();
        };
        document.addEventListener('contextmenu', blockContextmenu);
        document.addEventListener('keydown', blockShortcut);
        return () => {
            document.removeEventListener('contextmenu', blockContextmenu);
            document.removeEventListener('keydown', blockShortcut);
        };
    }, []);

    // Track the stage's available content width so pages can auto-fit it. A ResizeObserver
    // keeps it correct across window resizes and when the vertical scrollbar appears/hides
    // (which changes the usable width). The small inset leaves room for that scrollbar.
    useLayoutEffect(() => {
        const el = stageRef.current;
        if (!el) return;
        const measure = () => {
            const styles = getComputedStyle(el);
            const padX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
            setFitWidth(Math.max(200, el.clientWidth - padX - 16));
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
        setNumPages(numPages);
    }

    // Czech, branded states for react-pdf instead of its English defaults.
    const loadingState = (
        <div className="viewerMsg">
            <div className="spinner" />
            <p>Načítání dokumentu…</p>
        </div>
    );
    const errorState = (
        <div className="viewerMsg">
            <svg className="msgIcon" viewBox="0 0 24 24" width="46" height="46" fill="none"
                stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                <path d="M14 3v5h5" />
                <line x1="9.5" y1="15" x2="14.5" y2="15" />
            </svg>
            <p>Dokument se nepodařilo načíst.</p>
            <Link to=".." className="msgBack">← Zpět na lekci</Link>
        </div>
    );

    const clampZoom = (z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
    const pageWidth = fitWidth > 0 ? Math.round(fitWidth * zoom) : undefined;

    return (
        <div className="documentViewer" >
            <div className="viewerStage" ref={stageRef}>
                {failed
                    ? errorState
                    : <Document
                        file={file}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={() => setFailed(true)}
                        loading={loadingState}
                        error={errorState}
                        noData={loadingState}>
                        {pageWidth && Array.apply(null, Array(numPages))
                            .map((x, i) => i + 1)
                            .map(page => <Page key={page} pageNumber={page} width={pageWidth} renderTextLayer={false} renderAnnotationLayer={false} />)}
                        {email && numPages && <div className="watermark" style={watermarkStyle(email)} />}
                    </Document>
                }
            </div>

            {!failed && (
                <div className="zoomBar">
                    <button type="button" className="zoomBtn" aria-label="Zmenšit" onClick={() => setZoom(z => clampZoom(z - 0.2))}>−</button>
                    <input type="range" min={ZOOM_MIN * 100} max={ZOOM_MAX * 100} value={zoom * 100} className="slider" onChange={(x) => setZoom(parseFloat(x.target.value) / 100)} />
                    <button type="button" className="zoomBtn" aria-label="Zvětšit" onClick={() => setZoom(z => clampZoom(z + 0.2))}>+</button>
                    <button type="button" className={"fitBtn" + (Math.abs(zoom - 1) < 0.001 ? " active" : "")} aria-label="Přizpůsobit šířce" title="Přizpůsobit šířce" onClick={() => setZoom(1)}>⤢</button>
                </div>
            )}

            <Link to=".." className="back"><img src="/images/common/back.png" alt="" /></Link>
        </div>
    )
}
